import { FormEvent, useEffect, useRef, useState } from 'react';
import api from '../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../Component/server';
import { AdminActionButton } from '../../../Component/UI/AdminPageHeader';
import PagingControl from '../../../Component/UI/PagingControl';
import ProductThumbnail from '../../../Component/UI/ProductThumbnail';
import { mediaUrl } from '../../../Component/tool/mediaUrl';
import LiveDetailDialog from '../LiveList/Component/LiveDetailDialog';
import { productRequestError } from '../LiveList/productRequestError';
import './WarehouseProducts.css';
import { printProductQr } from './printProductQr';

export const WAREHOUSE_URL = `${SERVER_ADDRESS}product/warehouse`;
type SelectedProduct = { seq: number; name: string };
type Group = { seq: number; name: string; product_count: number };
type WarehouseRow = SelectedProduct & {
  price: number; product_num: string; options?: { code: string; name: string }[]; init_cnt: number; current_cnt?: number; live_seq: number | null;
  live_title: string | null; live_start: string | null; thumbnail_img: string | null;
  option_check: string; groups: Group[]; last_live_seq?: number; last_live_title?: string; last_live_at?: string; last_live_status?: string;
};
type Filters = { groups: Group[]; lives: { seq: number; title: string; date: string; product_count?: number }[]; direct_product_count?: number };

const productCountLabel = (count?: number) => count == null ? '' : ` (상품 ${Number(count).toLocaleString()}개)`;

export function defaultWarehouseGroupName(firstName: string, now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(now);
  const value = (type: string) => parts.find(part => part.type === type)?.value || '';
  return `${firstName}_${value('year')}${value('month')}${value('day')}_${value('hour')}${value('minute')}${value('second')}`;
}

export default function WarehouseProducts({ refreshVersion = 0, deletedGroupSeq, importLiveSeq, onImported, onBusyChange }: {
  refreshVersion?: number; deletedGroupSeq?: number; importLiveSeq?: number; onImported?: (count: number) => void | Promise<void>; onBusyChange?: (busy: boolean) => void;
}) {
  const [items, setItems] = useState<WarehouseRow[]>([]);
  const [filters, setFilters] = useState<Filters>({ groups: [], lives: [] });
  const [filterError, setFilterError] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState({ search: '', liveSeq: '', groupSeq: '', stock: '' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [version, setVersion] = useState(0);
  const [selected, setSelected] = useState(new Map<number, SelectedProduct>());
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [existingGroup, setExistingGroup] = useState<number[]>([]);
  const [groupError, setGroupError] = useState('');
  useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);
  const pageSize = 50;
  const params = { search: query.search || undefined, liveSeq: query.liveSeq === '' ? undefined : Number(query.liveSeq),
    groupSeq: query.groupSeq === '' ? undefined : Number(query.groupSeq), excludeLiveSeq: importLiveSeq, stock: query.stock || undefined };
  const sourceLives = filters.lives.filter(live => Number(live.seq) !== importLiveSeq);
  const sourceTotal = filters.direct_product_count == null || sourceLives.some(live => live.product_count == null) ? undefined
    : sourceLives.reduce((count, live) => count + Number(live.product_count), Number(filters.direct_product_count));

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(''); setItems([]);
    api.get(WAREHOUSE_URL, { params: { ...params, page, pageSize }, signal: controller.signal })
      .then(response => {
        if (controller.signal.aborted) return;
        if (!Array.isArray(response.data?.items) || !Number.isFinite(Number(response.data.total))) throw new Error('상품 목록을 확인할 수 없습니다.');
        setItems(response.data.items); setTotal(Number(response.data.total));
      }).catch(reason => { if (!controller.signal.aborted) { setTotal(0); setError(productRequestError(reason, '상품 창고를 불러오지 못했습니다.')); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
    // Filter values are primitives; params is rebuilt for requests only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.search, query.liveSeq, query.groupSeq, query.stock, page, version, refreshVersion, importLiveSeq]);

  useEffect(() => {
    const controller = new AbortController();
    setFilterError('');
    api.get(`${WAREHOUSE_URL}/filters`, { signal: controller.signal }).then(response => {
      if (controller.signal.aborted) return;
      if (!Array.isArray(response.data?.groups) || !Array.isArray(response.data?.lives)) throw new Error('필터 목록을 확인할 수 없습니다.');
      setFilters(response.data);
    }).catch(reason => { if (!controller.signal.aborted) setFilterError(productRequestError(reason, '필터 목록을 불러오지 못했습니다.')); });
    return () => controller.abort();
  }, [version, refreshVersion]);

  useEffect(() => {
    if (deletedGroupSeq && query.groupSeq === String(deletedGroupSeq)) {
      setQuery(previous => ({ ...previous, groupSeq: '' })); setPage(1); setSelected(new Map());
    }
  }, [deletedGroupSeq, query.groupSeq]);

  const changeFilter = (next: typeof query) => { setQuery(next); setPage(1); setSelected(new Map()); setNotice(''); };
  const submit = (event: FormEvent) => { event.preventDefault(); changeFilter({ ...query, search: search.trim() }); };
  const toggle = (product: SelectedProduct) => {
    if (!selected.has(Number(product.seq)) && selected.size >= 5000) { setError('한 번에 최대 5,000개 상품까지 선택할 수 있습니다.'); return; }
    setSelected(previous => {
      const next = new Map(previous);
      next.has(Number(product.seq)) ? next.delete(Number(product.seq)) : next.set(Number(product.seq), product);
      return next;
    });
  };
  const allPageSelected = items.length > 0 && items.every(item => selected.has(Number(item.seq)));
  const togglePage = () => {
    if (!allPageSelected && selected.size + items.filter(item => !selected.has(Number(item.seq))).length > 5000) { setError('한 번에 최대 5,000개 상품까지 선택할 수 있습니다.'); return; }
    setSelected(previous => {
      const next = new Map(previous);
      items.forEach(item => allPageSelected ? next.delete(Number(item.seq)) : next.set(Number(item.seq), item));
      return next;
    });
  };
  const selectAll = async () => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError('');
    try {
      const response = await api.get(`${WAREHOUSE_URL}/selection`, { params });
      if (!Array.isArray(response.data)) throw new Error('상품 목록을 확인할 수 없습니다.');
      setSelected(new Map(response.data.map((item: SelectedProduct) => [Number(item.seq), item])));
    } catch (reason) { setError(productRequestError(reason, '전체 상품을 선택하지 못했습니다.')); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const openGroup = () => {
    if (!selected.size) return;
    setGroupName(defaultWarehouseGroupName(Array.from(selected.values())[0].name));
    setExistingGroup([]); setGroupError(''); setGroupOpen(true);
  };
  const printQr = async () => {
    if (busyRef.current || !selected.size) return;
    busyRef.current = true; setBusy(true); setError('');
    try { await printProductQr(Array.from(selected.keys())); }
    catch (reason) { setError(productRequestError(reason, 'QR 인쇄를 준비하지 못했습니다.')); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const saveGroup = async (event: FormEvent) => {
    event.preventDefault();
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setGroupError('');
    try {
      const productSeqs = Array.from(selected.keys());
      if (existingGroup.length) await api.post(`${WAREHOUSE_URL}/groups/assign`, { productSeqs, groupSeqs: existingGroup });
      else await api.post(`${WAREHOUSE_URL}/groups`, { name: groupName.trim(), productSeqs });
      setGroupOpen(false); setSelected(new Map()); setNotice('선택한 상품에 그룹 태그를 적용했습니다.'); setVersion(value => value + 1);
    } catch (reason) { setGroupError(productRequestError(reason, '그룹을 저장하지 못했습니다.')); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const importProducts = async (wholeGroup = false) => {
    if (busyRef.current || !importLiveSeq) return;
    busyRef.current = true; setBusy(true); setError('');
    try {
      const response = await api.post(`${WAREHOUSE_URL}/import`, { liveSeq: importLiveSeq,
        ...(wholeGroup ? { groupSeq: Number(query.groupSeq) } : { productSeqs: Array.from(selected.keys()) }) });
      if (response.data?.status !== 'completed') throw new Error('상품 등록 결과를 확인할 수 없습니다.');
      await onImported?.(Number(response.data.count));
    } catch (reason) { setError(productRequestError(reason, '상품을 불러오지 못했습니다.')); }
    finally { busyRef.current = false; setBusy(false); }
  };

  return <section className={`warehouse-products${importLiveSeq ? ' warehouse-products--import' : ''}`} aria-label="상품 창고 목록" aria-busy={busy || loading}>
    <form data-admin-search="" className="warehouse-filters" onSubmit={submit}>
      <label className="warehouse-search">상품명·상품 코드 검색<input type="search" value={search} placeholder="상품명 또는 상품 코드를 입력해주세요" disabled={busy} onChange={event => setSearch(event.target.value)} /></label>
      <AdminActionButton type="submit" disabled={busy}>검색</AdminActionButton>
      <label>그룹 태그<select value={query.groupSeq} disabled={busy} onChange={event => changeFilter({ ...query, groupSeq: event.target.value })}>
        <option value="">모든 그룹</option><option value="0">그룹 미지정</option>
        {filters.groups.map(group => <option key={group.seq} value={group.seq}>{group.name} ({group.product_count})</option>)}
      </select></label>
      <label>재고 상태<select value={query.stock} disabled={busy} onChange={event => changeFilter({ ...query, stock: event.target.value })}>
        <option value="">전체 재고</option><option value="in-stock">재고 있음</option><option value="sold-out">품절</option>
      </select></label>
      {importLiveSeq && <label>원본 방송<select value={query.liveSeq} disabled={busy} onChange={event => changeFilter({ ...query, liveSeq: event.target.value })}>
        <option value="">모든 방송·직접 등록{productCountLabel(sourceTotal)}</option><option value="0">창고 직접 등록{productCountLabel(filters.direct_product_count)}</option>
        {sourceLives.map(live => <option key={live.seq} value={live.seq}>{live.title} · #{live.seq}{productCountLabel(live.product_count)}</option>)}
      </select></label>}
      <AdminActionButton data-admin-search-reset="" disabled={busy} onClick={() => { setSearch(''); changeFilter({ search: '', liveSeq: '', groupSeq: '', stock: '' }); }}>초기화</AdminActionButton>
    </form>
    {filterError && <p role="alert" className="warehouse-error">{filterError} <button type="button" onClick={() => setVersion(value => value + 1)}>다시 시도</button></p>}
    <div className="warehouse-selection">
      <span>총 <strong>{total.toLocaleString()}</strong>개 · <strong>{selected.size.toLocaleString()}</strong>개 선택</span>
      <div><AdminActionButton disabled={busy || loading || !total || total > 5000} onClick={selectAll}>검색 결과 전체 선택</AdminActionButton>
        {selected.size > 0 && <AdminActionButton disabled={busy} onClick={() => setSelected(new Map())}>선택 해제</AdminActionButton>}
        {!importLiveSeq && <AdminActionButton $accented disabled={busy || !selected.size} onClick={openGroup}>그룹 태그 지정</AdminActionButton>}
        {!importLiveSeq && <AdminActionButton disabled={busy || !selected.size} onClick={printQr}>QR 인쇄</AdminActionButton>}
        {importLiveSeq && <><AdminActionButton $primary disabled={busy || !selected.size} onClick={() => importProducts()}>{busy ? '처리 중…' : `선택 ${selected.size}개 불러오기`}</AdminActionButton>
          {Number(query.groupSeq) > 0 && <AdminActionButton disabled={busy || loading || !total} onClick={() => importProducts(true)}>그룹 전체 불러오기</AdminActionButton>}</>}
      </div>
    </div>
    {notice && <p role="status" className="warehouse-notice">{notice}</p>}
    {error && <p role="alert" className="warehouse-error">{error} <button type="button" disabled={busy} onClick={() => setVersion(value => value + 1)}>다시 시도</button></p>}
    <div className="warehouse-table-scroll" role={importLiveSeq ? "region" : undefined} aria-label={importLiveSeq ? "상품 목록 표" : undefined} tabIndex={importLiveSeq ? 0 : undefined}><table>
      <thead><tr><th><label className="warehouse-checkbox"><input type="checkbox" aria-label="현재 페이지 전체 선택" checked={allPageSelected} disabled={busy || loading || !items.length} onChange={togglePage} /></label></th><th>상품</th><th>상품 코드</th><th>옵션</th><th>가격</th><th>남은 재고</th><th>그룹 태그</th><th>마지막 방송</th></tr></thead>
      <tbody>{loading ? <tr><td colSpan={8} role="status">상품 목록을 불러오는 중…</td></tr> : !items.length ? <tr><td colSpan={8}>{error ? '목록을 다시 불러와주세요.' : '조건에 맞는 상품이 없습니다.'}</td></tr> : items.map(item => <tr key={item.seq} data-selected={selected.has(Number(item.seq)) || undefined}>
        <td><label className="warehouse-checkbox"><input type="checkbox" aria-label={`${item.name} 선택`} checked={selected.has(Number(item.seq))} disabled={busy} onChange={() => toggle(item)} /></label></td>
        <td><div className="warehouse-product"><ProductThumbnail src={item.thumbnail_img ? mediaUrl(item.thumbnail_img) : undefined} alt={item.name} /><div><strong>{item.name}</strong></div></div></td>
        <td className="warehouse-code">{item.product_num || '—'}</td>
        <td className="warehouse-options">{item.options?.length ? item.options.map((entry, index) => <span key={index}>{entry.name || '—'}</span>) : '—'}</td>
        <td className="warehouse-number">{Number(item.price).toLocaleString()}원</td><td className="warehouse-number">{Number(item.current_cnt ?? item.init_cnt).toLocaleString()}개</td>
        <td><div className="warehouse-tags">{item.groups.length ? item.groups.map(group => <button type="button" key={group.seq} disabled={busy} onClick={() => changeFilter({ ...query, groupSeq: String(group.seq) })}>{group.name}</button>) : <small>미지정</small>}</div></td>
        <td className="warehouse-last-live">{item.last_live_seq ? <><span>{item.last_live_title || '제목 없음'}</span><small>{item.last_live_status === '예약' ? '예정 · ' : ''}{item.last_live_at ? new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(item.last_live_at)) : '일시 미등록'}</small></> : <small>사용 이력 없음</small>}</td>
      </tr>)}</tbody>
    </table></div>
    {!busy && total > pageSize && <div className="warehouse-pagination"><PagingControl cnt={total} currentPage={page} setCurrentPage={setPage} perPage={pageSize} /></div>}
    <LiveDetailDialog open={groupOpen} title="그룹 태그 지정" description={`${selected.size}개 상품을 그룹으로 묶습니다.`} size="compact" closeOnlyByButton={busy} onClose={() => { if (!busyRef.current) setGroupOpen(false); }}>
      <form className="warehouse-group-form" onSubmit={saveGroup}>
        <fieldset className="warehouse-group-choices"><legend>기존 그룹 · 여러 개 선택 가능</legend>
          {filters.groups.map(group => <label key={group.seq}><input type="checkbox" checked={existingGroup.includes(Number(group.seq))} disabled={busy}
            onChange={event => setExistingGroup(previous => event.target.checked ? [...previous, Number(group.seq)] : previous.filter(id => id !== Number(group.seq)))} />{group.name}</label>)}
          {!filters.groups.length && <small>등록된 그룹이 없습니다.</small>}
        </fieldset>
        {!existingGroup.length && <label>새 그룹 이름<input value={groupName} maxLength={320} required disabled={busy} onChange={event => setGroupName(event.target.value)} /><small>기본 이름: 첫 번째 선택 상품명_현재 시간 (한국 시간)</small></label>}
        {groupError && <p role="alert" className="warehouse-error">{groupError}</p>}
        <AdminActionButton $primary type="submit" disabled={busy}>{busy ? '저장 중…' : '그룹 저장'}</AdminActionButton>
      </form>
    </LiveDetailDialog>
  </section>;
}
